import React, { useState, useEffect, useCallback } from "react";

import { useHistory, useLocation, useParams } from "react-router-dom";

import { Button, Flex, Box } from "@chakra-ui/react";

import { Form, Formik } from "formik";
import { FocusError } from "../../../components/FocusError";
import { SaudeCadastrar } from "./Cadastrar";
import { VisualizarPageSaude } from "./Visualizar";

import * as Yup from "yup";
import { useAppContext } from "../../../../Context/MessageContextProvider";
import { hasFormChanged } from "../../../utils/hasFormChanged";
import { getBase64 } from "../../../components/Base64";
import CriancaService from "../../../../main/services/CriancaService";
import Swal from "sweetalert2";
import { criancaFunc, Permission } from "../../../../helpers/Funcionalidades";
import { useAuthProvider } from "../../../../Context/AuthProvider";
import SemRegistros from "../../../components/SemRegistros";
import { scrollToFieldAnexo } from "../../../utils/scrollToFIeldAnexo";

const suportedFormats = [
  "image/png",
  "image/bmp",
  "image/tiff",
  "image/jpg",
  "image/jpeg",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "application/msword",
  "application/pdf",
  "application/xml",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.addin.macroEnabled.12",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const schema = Yup.object().shape({
  deficiencias: Yup.array().when("possuiDeficiencia", {
    is: "S",
    then: Yup.array()
      .required("Este campo é obrigatório!")
      .test(
        "não-vazio",
        "Este campo é obrigatório!",
        (value) => value && value[0].length > 0
      ),
    otherwise: Yup.array(),
  }),
  tratamentos: Yup.array().when("possuiProblemasSaude", {
    is: "S",
    then: Yup.array()
      .of(
        Yup.object().shape({
          fazTratamento: Yup.string()
            .nullable()
            .required("Este campo é obrigatório!"),
          doenca: Yup.object().nullable().required("Este campo é obrigatório!"),
          laudoMedico: Yup.mixed()
            .nullable()
            .test("imageType", "Anexar uma extensão válida.", (value) => {
              if (value === null) {
                return true;
              } else {
                return suportedFormats.includes(value.type);
              }
            })
            .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
              if (value === null) {
                return true;
              } else {
                return value.size <= 10000000;
              }
            }),
        })
      )
      .required("Por favor, adicione e informe o problema de saúde")
      .min(1, "Por favor, adicione e informe o problema de saúde"),
    otherwise: Yup.array().of(
      Yup.object().shape({
        fazTratamento: Yup.string().nullable(),
        doenca: Yup.object().nullable(),
        laudoMedico: Yup.mixed()
          .nullable()
          .test("imageType", "Anexar uma extensão válida.", (value) => {
            if (value === null) {
              return true;
            } else {
              return suportedFormats.includes(value.type);
            }
          })
          .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
            if (value === null) {
              return true;
            } else {
              return value.size <= 10000000;
            }
          }),
      })
    ),
  }),
  medicacaoContinua: Yup.string().when("possuiProblemasSaude", {
    is: "S",
    then: Yup.string().required("Este campo é obrigatório!"),
    otherwise: Yup.string(),
  }),
  possuiDeficiencia: Yup.string().required("Este campo é obrigatório"),
  possuiProblemasSaude: Yup.string().required("Este campo é obrigatório"),
  cartaoVacinacao: Yup.string().required("Este campo é obrigatório").nullable(),
});

export const VisualizarSaude = () => {
  const { id } = useParams();
  const [isTrueVisualizar, setIsTrueVisualizar] = useState(true);
  const { roleKeycloack, user } = useAuthProvider();

  const history = useHistory();
  const url = window.location.href;
  const {
    atualizarSaude,
    setAtualizarSaude,
    cadastrarSaude,
    setCadastrarSaude,
    setEditarSaudeCrianca,
    setCadastrarSaudeCrianca,
    setEditarCriancaCr,
    radioClick,
    setRadioClick,
    setAnexarCrianca,
    idPJ,
  } = useAppContext();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idAnexo = searchParams.get("idAnexo");
  const idTipoPendencia = searchParams.get("idTipoPendencia");

  const scrollToField = useCallback(
    (ref) => {
      scrollToFieldAnexo(idAnexo || idTipoPendencia, ref);
    },
    [idAnexo, idTipoPendencia]
  );

  useEffect(() => {
    if (url.includes("visualizar")) {
      setIsTrueVisualizar(true);
      setRadioClick("");
    }
  }, [setRadioClick, url]);

  useEffect(() => {
    if (url.includes("editar")) {
      setRadioClick("Editar");
    }
  }, [url, setRadioClick]);

  useEffect(() => {
    setEditarCriancaCr(false);
    setAnexarCrianca(false);
  }, [setEditarCriancaCr, setAnexarCrianca]);

  const [initialValues, setInitialValues] = useState({
    deficiencias: "",
    tratamentos: [{ fazTratamento: "", doenca: "", laudoMedico: null }],
    medicacaoContinua: "",
    possuiDeficiencia: "",
    possuiProblemasSaude: "",
    cartaoVacinacao: "",
    anexoCartaoVacinacao: null,
  });

  useEffect(() => {
    if (atualizarSaude) {
      Swal.fire({
        title: "Sucesso!",
        text: "Registro atualizado com sucesso!",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {
        setAtualizarSaude(false);
        setEditarSaudeCrianca(false);
      });
    } else if (cadastrarSaude) {
      Swal.fire({
        title: "Sucesso!",
        text: "Registro cadastrado com sucesso!",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {
        setCadastrarSaude(false);
        setCadastrarSaudeCrianca(false);
      });
    }
  }, [
    setEditarSaudeCrianca,
    atualizarSaude,
    setAtualizarSaude,
    cadastrarSaude,
    setCadastrarSaude,
    setCadastrarSaudeCrianca,
  ]);

  const onKeyDown = (keyEvent) => {
    if (keyEvent.code === "Enter" || keyEvent.code === "NumpadEnter") {
      keyEvent.preventDefault();
    }
  };

  const handleSubmit = (values, actions) => {
    setTimeout(async () => {
      const {
        deficiencias,
        medicacaoContinua,
        possuiDeficiencia,
        possuiProblemasSaude,
        tratamentos,
        cartaoVacinacao,
        anexoCartaoVacinacao,
      } = values;

      const tratamentosComArquivos = await Promise.all(
        tratamentos.map(async (tratamento) => {
          let arquivoBase64Value = tratamento.laudoMedico;

          if (tratamento.laudoMedico instanceof File) {
            arquivoBase64Value = await new Promise((resolve) => {
              getBase64(tratamento.laudoMedico, (base64Data) => {
                resolve(base64Data);
              });
            });
          }

          const nameImage = tratamento.laudoMedico?.name;
          const typeImage = tratamento.laudoMedico?.type;
          const idAnexoImage = tratamento.laudoMedico?.idAnexo;

          const prevLaudo = {
            arquivoBase64: "",
            arquivoNome: "",
            arquivoTipo: "",
            atualizar: false,
            idAnexo: idAnexoImage ? idAnexoImage : 0,
            remover: true,
          };

          if (arquivoBase64Value) {
            prevLaudo.arquivoBase64 = arquivoBase64Value;
            prevLaudo.arquivoNome = nameImage;
            prevLaudo.arquivoTipo = typeImage;
            prevLaudo.atualizar = idAnexoImage ? false : true;
            prevLaudo.idAnexoImag = idAnexoImage ? idAnexoImage : 0;
            prevLaudo.remover = false;
          }

          return {
            ...tratamento,
            idSaudeDoenca: tratamento.idSaudeDoenca
              ? tratamento.idSaudeDoenca
              : 0,
            laudoMedico: prevLaudo,
          };
        })
      );

      let anexoCartaoBase64 = null;

      if (anexoCartaoVacinacao instanceof File) {
        anexoCartaoBase64 = await new Promise((resolve) => {
          getBase64(anexoCartaoVacinacao, (base64) => resolve(base64));
        });
      }

      const nameImageV = anexoCartaoVacinacao?.name;
      const typeImageV = anexoCartaoVacinacao?.type;
      const idAnexoImageV = anexoCartaoVacinacao?.idAnexo;

      const prevCartaoV = {
        arquivoBase64: "",
        arquivoNome: "",
        arquivoTipo: "",
        atualizar: false,
        idAnexo: idAnexoImageV ? idAnexoImageV : 0,
        remover: true,
      };

      if (anexoCartaoBase64 && cartaoVacinacao === "S") {
        prevCartaoV.arquivoBase64 = anexoCartaoBase64;
        prevCartaoV.arquivoNome = nameImageV;
        prevCartaoV.arquivoTipo = typeImageV;
        prevCartaoV.atualizar = idAnexoImageV ? false : true;
        prevCartaoV.idAnexoImag = idAnexoImageV ? idAnexoImageV : 0;
        prevCartaoV.remover = false;
      }

      const valuesToSend = {
        deficiencias: possuiDeficiencia === "S" ? deficiencias[0] : null,
        tratamentos: possuiProblemasSaude === "S" ? tratamentosComArquivos : [],

        medicacaoContinua:
          possuiProblemasSaude === "S" ? medicacaoContinua : "",
        possuiDeficiencia: possuiDeficiencia,
        possuiProblemasSaude: possuiProblemasSaude,
        cartaoVacinacao: cartaoVacinacao,
        anexoCartaoVacinacao: prevCartaoV,
      };

      if (isTrueVisualizar) {
        setRadioClick("Editar");
        setIsTrueVisualizar(!isTrueVisualizar);
        history.push(`/crianca/editar-saude/${id}`);
      } else {
        if (radioClick === "Editar") {
          CriancaService.putCriancaSaude(id, valuesToSend)
            .then((res) => {
              history.push(`/crianca/visualizar-saude/${id}`);
              setIsTrueVisualizar(true);
              setRadioClick("");
              setEditarSaudeCrianca(false);
              setCadastrarSaudeCrianca(false);

              setAtualizarSaude(true);
            })
            .catch((error) => {
              console.log(error.response.data);
              Swal.fire({
                title: "Atenção!",
                text: error.response.data.message
                  ? error.response.data.message
                  : "",
                icon: "error",
                confirmButtonText: "Fechar",
                confirmButtonColor: "#169499",
              });
            });
        } else {
          CriancaService.postCriancaSaude(id, valuesToSend)
            .then((res) => {
              history.push(`/crianca/visualizar-saude/${id}`);
              setIsTrueVisualizar(true);
              setRadioClick("");
              setCadastrarSaudeCrianca(false);
              setEditarSaudeCrianca(false);

              setCadastrarSaude(true);
            })
            .catch((error) => {
              console.log(error.response.data);
              Swal.fire({
                title: "Atenção!",
                text: error.response.data.message
                  ? error.response.data.message
                  : "",
                icon: "error",
                confirmButtonText: "Fechar",
                confirmButtonColor: "#169499",
              });
            });
        }
      }

      actions.setSubmitting(false);
    });
  };

  return roleKeycloack &&
    (roleKeycloack?.includes(criancaFunc.visualizarSaude) ||
      ((roleKeycloack?.includes(criancaFunc.visualizarTudoAcolhimento) ||
        roleKeycloack?.includes(criancaFunc.visualizarTudoTutelar)) &&
        user?.orgaoExterno?.includes(idPJ))) ? (
    <>
      <Flex flex={1} flexDir="column" alignItems="center" overflow="auto">
        <Flex flexDir="column" w="100%">
          <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
          >
            {(props) => (
              <Form onKeyDown={onKeyDown}>
                {setEditarSaudeCrianca(
                  hasFormChanged(initialValues, props.values)
                )}
                <FocusError />
                {isTrueVisualizar ? (
                  <VisualizarPageSaude
                    id={id}
                    setIsTrueVisualizar={setIsTrueVisualizar}
                    setRadioClick={setRadioClick}
                    setInitialValues={setInitialValues}
                    url={url}
                  />
                ) : roleKeycloack &&
                  (roleKeycloack?.includes(criancaFunc.cadastrarSaude) ||
                    ((roleKeycloack?.includes(
                      criancaFunc.visualizarTudoAcolhimento
                    ) ||
                      roleKeycloack?.includes(
                        criancaFunc.visualizarTudoTutelar
                      )) &&
                      user?.orgaoExterno?.includes(idPJ))) ? (
                  <SaudeCadastrar
                    initialValues={initialValues}
                    valuesProps={props.values}
                    idAnexo={idAnexo}
                    idTipoPendencia={idTipoPendencia}
                    scrollToField={scrollToField}
                  />
                ) : (
                  <Box mt={4}>
                    <SemRegistros buttonVisible={true} half />
                  </Box>
                )}
                <Flex justifyContent="flex-end">
                  <Flex justifyContent="flex-end">
                    <Permission
                      roleKeycloack={roleKeycloack}
                      func={criancaFunc.editarSaude}
                      user={user}
                      id={idPJ}
                      visualizar={true}
                    >
                      <Button
                        type={isTrueVisualizar ? "button" : "submit"}
                        translate="no"
                        mt="30px"
                        px="45px"
                        h="42px"
                        color="#FFF"
                        backgroundColor="#169499"
                        fontWeight={700}
                        fontSize="15px"
                        lineHeight="21.6px"
                        onClick={() => {
                          isTrueVisualizar &&
                            history.push(`/crianca/editar-saude/${id}`);
                        }}
                        _hover={{ bg: "#169499" }}
                        isLoading={props.isSubmitting}
                      >
                        {isTrueVisualizar ? "Editar" : "Salvar"}
                      </Button>
                    </Permission>
                  </Flex>
                </Flex>
              </Form>
            )}
          </Formik>
        </Flex>
      </Flex>
    </>
  ) : (
    <Box mt={2}>
      <SemRegistros buttonVisible={true} half />
    </Box>
  );
};
